import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import ConfirmationModal from "./ConfirmationModal";
import "./HomePage.css";
import authService from "../services/authService";

interface HomePageProps {
  user?: {
    userid: string;
    email: string;
    name: string;
    companyName?: string;
  };
  onLogout: () => void;
}

const HomePage: React.FC<HomePageProps> = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [showInvalidSettingsWarning, setShowInvalidSettingsWarning] =
    useState(false);

  const handleCardClick = async (cardType: string) => {
    switch (cardType) {
      case "admin-settings":
        navigate("/settings");
        break;
      case "mapping-settings":
        await checkValidSettingsAndNavigate();
        break;
      case "registration-history":
        navigate("/batch_history");
        break;
      default:
        break;
    }
  };

  const checkValidSettingsAndNavigate = async () => {
    try {
      const response = await authService.getMe();
      if (
        response.success &&
        response.data?.appCd &&
        response.data?.isHasCollaboAPIKey === true &&
        response.data?.isHasCollaboUserid === true
      ) {
        // App_cd exists, navigate normally
        navigate("/mapping_registration_list");
      } else {
        // App_cd doesn't exist or failed to get, show warning
        setShowInvalidSettingsWarning(true);
      }
    } catch (error: any) {
      console.error("Error checking app_cd:", error);

      // Handle timeout errors specifically
      if (error.isTimeout) {
        // For timeout, don't show warning popup, just log the error
        // The timeout toast is already shown by the service
        setShowInvalidSettingsWarning(false);
        return;
      }

      // On other errors, show warning to be safe
      setShowInvalidSettingsWarning(true);
    }
  };

  const handleGoToSettings = () => {
    setShowInvalidSettingsWarning(false);
    navigate("/settings");
  };

  const handleCancelWarning = () => {
    setShowInvalidSettingsWarning(false);
  };

  const handleLogoClick = () => {
    navigate("/");
  };

  return (
    <div className="home-page">
      {/* Header */}
      <header className="home-header">
        <div className="header-content">
          <div className="logo-section">
            <img 
              src="/images/logo.png" 
              alt="コラボフロー for 商奉行 ロゴ" 
              className="app-logo"
            />
            <div className="title-user-wrapper">
              <h1
                className="app-title clickable-logo"
                onClick={handleLogoClick}
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleLogoClick();
                  }
                }}
                aria-label="ホームページに戻る"
              >
                コラボフロー for 商奉行
              </h1>
              <div className="user-info">
                <span className="user-id">
                  {user?.companyName && (
                    <span className="instance-name">インスタンス名: {user.companyName}</span>
                  )}
                  ユーザーID: {user?.userid || "admin_user_001"}
                </span>
              </div>
            </div>
          </div>
          <div className="user-section">
            <button
              className="logout-btn"
              onClick={() => {
                // Use default browser popup by triggering beforeunload
                const event = new Event("beforeunload");
                window.dispatchEvent(event);
                onLogout();
              }}
              title="ログアウト"
            >
              ログアウト
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="home-main">
        <div className="cards-container">
          {/* Administrator Settings Card */}
          <div
            className="feature-card"
            onClick={() => handleCardClick("admin-settings")}
            role="button"
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleCardClick("admin-settings");
              }
            }}
            aria-label="管理者設定ページへ移動"
          >
            <div className="card-content">
              <h3 className="card-title">管理者設定</h3>
              <p className="card-description">
                APIキー、アクセストークン、システムAPIキーの発行などを管理します
              </p>
            </div>
          </div>

          {/* Mapping Settings Card */}
          <div
            className="feature-card"
            onClick={() => handleCardClick("mapping-settings")}
            role="button"
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleCardClick("mapping-settings");
              }
            }}
            aria-label="マッピング設定ページへ移動"
          >
            <div className="card-content">
              <h3 className="card-title">マッピング設定</h3>
              <p className="card-description">
                コラボフロー申請書項目と商奉行データとのマッピング設定を実施します
              </p>
            </div>
          </div>

          {/* Registration History Card */}
          <div
            className="feature-card"
            onClick={() => handleCardClick("registration-history")}
            role="button"
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleCardClick("registration-history");
              }
            }}
            aria-label="連携ログページへ移動"
          >
            <div className="card-content">
              <h3 className="card-title">連携ログ</h3>
              <p className="card-description">
                商奉行への登録履歴とエラー内容を確認します
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* App CD Warning Modal */}
      <ConfirmationModal
        isOpen={showInvalidSettingsWarning}
        title="設定が必要です"
        message={`設定されたコラボフローの認証情報が正しくないか、無効になっている可能性があります。管理者設定でご確認ください。`}
        confirmText="管理者設定へ"
        cancelText="キャンセル"
        confirmButtonColor="primary"
        onConfirm={handleGoToSettings}
        onCancel={handleCancelWarning}
      />
    </div>
  );
};

export default HomePage;
